import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  streamingAccountService,
  StreamingAccount,
  UpdateStreamingAccountRequest,
  EStreamingAccountStatus,
  EStreamingAccountType,
} from "@/services/streaming-account";
import { Supplier } from "@/services/supplier";
import { SupplierCombobox } from "@/components/shared/SupplierCombobox";
import { Loader2, Truck, LayoutList, HelpCircle } from "lucide-react";

interface SlotEdit {
  id: number;
  name: string;
  pin: string;
}

interface UpdatePayload {
  accountData: UpdateStreamingAccountRequest;
  changedSlots: SlotEdit[];
}

interface UpdateStreamingAccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: StreamingAccount | null;
}

export function UpdateStreamingAccountModal({
  open,
  onOpenChange,
  account,
}: UpdateStreamingAccountModalProps) {
  const queryClient = useQueryClient();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [type, setType] = useState<EStreamingAccountType>(EStreamingAccountType.SHARED);
  const [status, setStatus] = useState<EStreamingAccountStatus>(EStreamingAccountStatus.ACTIVE);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [slots, setSlots] = useState<SlotEdit[]>([]);
  const [originalSlots, setOriginalSlots] = useState<Record<number, { name: string; pin: string }>>({});
  const [isNotify, setIsNotify] = useState(false);

  useEffect(() => {
    if (open && account) {
      setEmail(account.email);
      setPassword("");
      setType(account.type);
      setStatus(account.status);
      setSelectedSupplier(
        account.supplier ? (account.supplier as unknown as Supplier) : null,
      );
      const slotList = account.slots.map((s) => ({ id: s.id, name: s.name, pin: s.pin || "" }));
      setSlots(slotList);
      const originals: Record<number, { name: string; pin: string }> = {};
      account.slots.forEach((s) => {
        originals[s.id] = { name: s.name, pin: s.pin || "" };
      });
      setOriginalSlots(originals);
      setIsNotify(false);
    }
  }, [open, account]);

  const updateMutation = useMutation({
    mutationFn: async ({ accountData, changedSlots }: UpdatePayload) => {
      await streamingAccountService.update(String(account!.id), accountData);
      if (changedSlots.length > 0) {
        await Promise.all(
          changedSlots.map((s) =>
            streamingAccountService.updateSlot(s.id, {
              name: s.name.trim(),
              pin: s.pin.trim(),
            }),
          ),
        );
      }
    },
    onSuccess: () => {
      toast.success("Cuenta actualizada correctamente");
      queryClient.invalidateQueries({
        queryKey: ["streaming-account", String(account!.id)],
      });
      queryClient.invalidateQueries({ queryKey: ["streaming-accounts"] });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error("El email es requerido");
      return;
    }

    const changedSlots = slots.filter((s) => {
      const original = originalSlots[s.id];
      if (!s.name.trim()) return false;
      return (
        original?.name !== s.name.trim() || original?.pin !== s.pin.trim()
      );
    });

    const accountData: UpdateStreamingAccountRequest = {
      email: email.trim(),
      type,
      status,
      isNotify,
      ...(password ? { password } : {}),
      ...(selectedSupplier ? { supplierId: Number(selectedSupplier.id) } : {}),
    };

    updateMutation.mutate({ accountData, changedSlots });
  };

  const handleSlotNameChange = (slotId: number, name: string) => {
    setSlots((prev) => prev.map((s) => (s.id === slotId ? { ...s, name } : s)));
  };

  const handleSlotPinChange = (slotId: number, pin: string) => {
    setSlots((prev) => prev.map((s) => (s.id === slotId ? { ...s, pin } : s)));
  };

  const isPending = updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Cuenta de Streaming</DialogTitle>
          <DialogDescription>
            Actualiza la información de la cuenta de streaming.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ── Datos de la cuenta ── */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email o usuario *</Label>
              <Input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="cuenta@ejemplo.com"
                disabled={isPending}
              />
            </div>

            <div className="space-y-2">
              <Label>Contraseña</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Dejar vacío para no cambiar"
                disabled={isPending}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select
                  value={type}
                  onValueChange={(v) => setType(v as EStreamingAccountType)}
                  disabled={isPending}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EStreamingAccountType.SHARED}>
                      Compartida
                    </SelectItem>
                    <SelectItem value={EStreamingAccountType.DEDICATED}>
                      Dedicada
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Estado *</Label>
                <Select
                  value={status}
                  onValueChange={(v) => setStatus(v as EStreamingAccountStatus)}
                  disabled={isPending}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EStreamingAccountStatus.ACTIVE}>
                      Activa
                    </SelectItem>
                    <SelectItem value={EStreamingAccountStatus.INACTIVE}>
                      Inactiva
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="notify-client"
                checked={isNotify}
                onCheckedChange={(checked) => setIsNotify(checked === true)}
                disabled={isPending}
              />
              <Label htmlFor="notify-client" className="text-sm font-normal cursor-pointer">
                Notificar al cliente
              </Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <HelpCircle className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[260px] text-sm">
                  Al guardar los cambios, se le enviará al cliente la información actualizada de la cuenta (correo y contraseña).
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* ── Proveedor ── */}
          <Separator />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-muted-foreground" />
              <Label>Proveedor</Label>
            </div>
            <SupplierCombobox
              value={selectedSupplier}
              onChange={setSelectedSupplier}
              disabled={isPending}
            />
          </div>

          {/* ── Slots ── */}
          {slots.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <LayoutList className="h-4 w-4 text-muted-foreground" />
                  <Label>Slots (nombre y PIN)</Label>
                </div>
                <div className="space-y-2">
                  {slots.map((slot, index) => (
                    <div key={slot.id} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-5 text-right shrink-0">
                        {index + 1}.
                      </span>
                      <Input
                        value={slot.name}
                        onChange={(e) =>
                          handleSlotNameChange(slot.id, e.target.value)
                        }
                        placeholder={`Slot ${index + 1}`}
                        className="h-8 text-sm"
                        disabled={isPending}
                      />
                      <Input
                        value={slot.pin}
                        onChange={(e) =>
                          handleSlotPinChange(slot.id, e.target.value)
                        }
                        placeholder="PIN"
                        className="h-8 text-sm w-20 shrink-0"
                        disabled={isPending}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isPending ? "Guardando..." : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
